import { useState, useRef, useMemo, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { i18n } from "../../../translate/i18n";

import * as FileSaver from "file-saver";

import { Default } from "../../../components/Default/Default";
import { LoadingContent } from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { CustomHeading, CustomText } from "../../../components/StyledComponents/styleComponents.index";

import { useOtbViewModel } from "../../../viewModel/useOtbVewModel";

import { 
  HeaderWithFilters, 
  HEADER_STRATEGY_CONSTANTS 
} from "../../../viewModel/strategy/headers/HeaderWithFilters/headerWithFilters.strategy";

import { createPercentFormatter, createIntFormatter, currencyFormatter } from "../../../utils/formatter/formatter";
import { COMPANY_STORE_ID, RETAIL_INVENTORY_QUALITY_TABS } from "../../../components/components.constants";
import { SYSTEM_COLORS } from "../../../components/system.colors";

import { useAnalysisViewModel } from "../../../viewModel/useAnalysysViewModel";

import { DeliveryIcon, CartIcon, GridIcon } from "./components/icons.index";

const { t } = i18n;

// funções utilitárias do módulo
const buildHierarchyInfo = ({ hierarchyInfo = {} }) => {
  if (!hierarchyInfo) return null;

  const { level, additionalLevels = {} } = hierarchyInfo;

  const sorted = Object.keys(additionalLevels)
    .sort((a, b) => {
      const numA = parseInt(a.replace('level', ''));
      const numB = parseInt(b.replace('level', ''));
      return numA - numB;
    })
    .map(key => additionalLevels[key])
    .filter(Boolean);

  return [level, ...sorted].filter(Boolean);
};

const downloadCSV = ({ data, fileName }) => {
  if (!data || !data.length) return;
  const csv = ['sku', ...data].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(blob, `${fileName}.csv`);
};

const areaFieldMap = {
  1: 'supplyAdjustmentPurchaseArea',
  2: 'supplyAdjustmentAssortmentArea',
  3: 'supplyAdjustmentRepleneshimentArea',
};

const AREA_EXPORT_LABELS = [
  { field: 'supplyAdjustmentAssortmentArea', label: 'Abastecimento' },
  { field: 'supplyAdjustmentPurchaseArea', label: 'Compra' },
  { field: 'supplyAdjustmentRepleneshimentArea', label: 'Sortimento' },
];

const getActiveAreasExportLabel = (original) => {
  const active = AREA_EXPORT_LABELS
    .filter(({ field }) => original?.[field])
    .map(({ label }) => label);
  return active.length ? active.join(', ') : '--';
};

const HEADER_HEIGHT = 43;

const TAB_STYLE_BASE = {
  padding: "0.5rem 1rem",
  border: "none",
  cursor: "pointer",
  backgroundColor: SYSTEM_COLORS.GRAY.ZINC_100,
  borderTop: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`,
  borderLeft: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`,
  borderRight: `1px solid ${SYSTEM_COLORS.GRAY.GRAY_200}`,
  borderRadius: "0.4rem 0.4rem 0 0",
  fontSize: "0.6rem"
};

const TAB_STYLE_ACTIVE = {
  fontWeight: 900,
  color: "#111827",
  borderBottom: "none",
};

const TAB_STYLE_INACTIVE = {
  fontWeight: 400,
  color: "#6B7280",
  borderTop: "none",
  borderBottom: "none",
  borderRight: "none",
  borderLeft: "1px solid #e5e7eb",
};

const TAB_STYLE = (isActive) => ({
  ...TAB_STYLE_BASE,
  ...(isActive ? TAB_STYLE_ACTIVE : TAB_STYLE_INACTIVE),
});

const overlayStyle = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(243, 244, 246, 0.1)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  backdropFilter: "blur(2px)",
};

const modalStyle = {
  backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
  borderRadius: "12px",
  padding: "32px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

const getColumnWidth = (columnId, baseWidthPct, lastColumnId) => {
  const LEVEL_WIDTH_FACTOR = 2.4;
  const LAST_COLUMN_WIDTH_FACTOR = 0.9;

  if (columnId === "level") return `${baseWidthPct * LEVEL_WIDTH_FACTOR}%`;
  if (columnId === lastColumnId) return `${baseWidthPct * LAST_COLUMN_WIDTH_FACTOR}%`;
  return undefined;
};

// componentes
const intensityColor = (value, min, max) => {
  const base = "196, 115, 72";
  if (max === min) return `rgba(${base}, 0.15)`;
  const ratio = (value - min) / (max - min);
  const alpha = 0.12 + ratio * 0.55;
  return `rgba(${base}, ${alpha.toFixed(2)})`;
};

const SupplyGradeAnalysisCard = ({ ctx }) => {

  const { levelEntry, familyLabel, productId } = ctx || {};

  const details = levelEntry?.details;

  if (!details || !Array.isArray(details.products) || details.products.length === 0) {
    return (
      <div style={{ padding: 12, fontSize: 12, color: '#111827', background: '#fff', borderRadius: 12 }}>
        Sem dados de grade para esta família.
      </div>
    );
  }

  const filteredProducts = productId != null
    ? details.products.filter(p => String(p.productId) === String(productId))
    : details.products;

  if (filteredProducts.length === 0) {
    return (
      <div style={{ padding: 12, fontSize: 12, color: '#111827', background: '#fff', borderRadius: 12 }}>
        Sem dados de grade para este produto.
      </div>
    );
  }

  const aggregateBySize = (products = [], field) =>
    products.reduce((acc, product) => {
      const map = product?.[field] ?? {};
      Object.entries(map).forEach(([size, value]) => {
        acc[size] = (acc[size] ?? 0) + Number(value);
      });
      return acc;
    }, {});

  const sizes = Array.from(
    filteredProducts.reduce((map, product) => {
      (product.sizes || []).forEach(s => {
        if (!map.has(s.sizeName)) map.set(s.sizeName, s);
      });
      return map;
    }, new Map())
    .values()
  ).sort((a, b) => a.sizeOrder - b.sizeOrder);

  const rows = [
    {
      key: 'quebra', label: 'Quebra',
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.ORANGE.BURNT_ORANGE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
      values: aggregateBySize(filteredProducts, 'salesBreakdownBySize'),
      format: (value) => createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value),
      color: 'F97316', pill: true
    },
    {
      key: 'giro', label: 'Giro',
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.ORANGE.BURNT_ORANGE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
      values: aggregateBySize(filteredProducts, 'turnoverBySize'),
      format: (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      color: 'F97316', pill: false
    },
    {
      key: 'loja', label: 'Est. loja',
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.BRICK_RED} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l1-5h16l1 5"/>
          <path d="M3 9v10a1 1 0 0 0 1 1h4v-6h8v6h4a1 1 0 0 0 1-1V9"/>
        </svg>
      ),
      values: aggregateBySize(filteredProducts, 'inventoryStoreBySize'),
      format: (v) => createIntFormatter().format(v),
      color: '64748B', pill: false
    },
    {
      key: 'cd', label: 'Est. CD',
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.BRICK_RED} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="1"/>
          <line x1="9" y1="7" x2="15" y2="7"/>
          <line x1="9" y1="11" x2="15" y2="11"/>
          <line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
      ),
      values: aggregateBySize(filteredProducts, 'inventoryCDBySize'),
      format: (v) => createIntFormatter().format(v),
      color: '64748B', pill: false
    },
  ];

  const quebraValues = rows[0].values;
  const worstSize = sizes.reduce((worst, size) => {
    const value = Number(quebraValues[size.sizeName] * 10) || 0;
    if (!worst || value > worst.value) return { sizeName: size.sizeName, value };
    return worst;
  }, null);

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: `0 10px 30px ${SYSTEM_COLORS.BLACK.BLACK_25_PERCENT}`,
      color: '#111827' }}>
      <div style={{
        background: SYSTEM_COLORS.ORANGE.DEEP_ORANGE,
        padding: '10px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: SYSTEM_COLORS.WHITE.WHITE,
      }}>
        <hgroup style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: "center", gap: "0.4rem" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
            </svg>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, opacity: 0.9 }}>
              ANÁLISE DA GRADE
            </p>            
          </div>

          <p style={{ fontSize: 13, fontWeight: 700 }}>{familyLabel}</p>
        </hgroup>

        {worstSize && (
          <hgroup style={{ 
            backgroundColor: SYSTEM_COLORS.ORANGE.LIGHT_BURNT_ORANGE, textAlign: 'right', padding: "0.4rem",
            borderRadius: "1rem" }}>
            <p style={{ fontSize: 10, fontWeight: 700, opacity: 0.85 }}>
              MAIOR QUEBRA
            </p>
            <p style={{ fontSize: 12, fontWeight: 700 }}>
              {`
              Tam ${worstSize.sizeName} · ${createPercentFormatter(
                  { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                ).format(worstSize.value / 10)}`
              }
            </p>
          </hgroup>
        )}
      </div>

      <div style={{ padding: '10px 14px', overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <td style={{ minWidth: 70 }} />
              {sizes.map(size => (
                <td 
                key={size.sizeName} 
                style={{ 
                  textAlign: 'center', fontSize: 11, fontWeight: 700, 
                  color: SYSTEM_COLORS.GRAY.SLATE_GRAY, padding: '6px 6px' }}>
                  {size.sizeName}
                </td>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map(row => {
              const nums = sizes.map(s => Number(row.values[s.sizeName]) || 0);
              const min = Math.min(...nums);
              const max = Math.max(...nums);

              return (
                <tr key={row.key}>
                  <td style={{ 
                    padding: '6px 24px 6px 6px', fontSize: 11, fontWeight: 600, color: SYSTEM_COLORS.GRAY.CHARCOAL_GRAY, 
                    whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: "0.4rem" }}>
                      {row.icon}
                      {row.label}
                    </span>
                  </td>

                  {sizes.map(size => {
                    const raw = row.values[size.sizeName];
                    const value = Number(raw);

                    if (raw === undefined || Number.isNaN(value)) {
                      return (
                        <td key={size.sizeName} 
                        style={{ textAlign: 'center', padding: '4px 6px', fontSize: 11, color: SYSTEM_COLORS.GRAY.BLUE_GRAY }}>
                          --
                        </td>
                      );
                    }

                    return (
                      <td key={size.sizeName} style={{ textAlign: 'center', padding: '6px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <span style={{
                            display: 'inline-block',
                            minWidth: 40,
                            padding: row.pill ? '6px' : '6px',
                            borderRadius: row.pill ? 999 : 4,
                            background: row.pill ? intensityColor(value, min, max) : SYSTEM_COLORS.GRAY.GAINSBORO,
                            color: SYSTEM_COLORS.BLACK.DARK_NAVY,
                            fontSize: 12,
                            fontWeight: 700,
                          }}>
                            {row.format(value)}
                          </span>

                          {!row.pill && (
                            <div style={{
                              width: 32,
                              height: 3,
                              borderRadius: 2,
                              background: SYSTEM_COLORS.GRAY.GAINSBORO,
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                width: max === min ? '100%' : `${((Number(value) - min) / (max - min)) * 100}%`,
                                height: '100%',
                                borderRadius: 2,
                                background: row.key === 'giro' ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE : SYSTEM_COLORS.GRAY.DARK_GRAY,
                              }} />
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ 
        padding: '6px 14px 10px', fontSize: 10, color: SYSTEM_COLORS.GRAY.SPANISH_GRAY,
        borderTop: `1px solid ${SYSTEM_COLORS.GRAY.ZINC_100}` }}>
        Intensidade da cor indica o nível relativo dentro da grade desta família.
      </p>
    </div>
  );
};

const TooltipBase = ({ children, content }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);

  const handleEnter = () => {
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({ top: rect.top - 8, left: rect.left });
    setVisible(true);
  };

  useLayoutEffect(() => {
    if (!visible || !tooltipRef.current || !anchorRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = anchorRect.left;
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));

    let top = anchorRect.top - tooltipRect.height - margin;
    if (top < margin) {
      top = anchorRect.bottom + margin;
    }
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

    setCoords({ top, left });
  }, [visible]);

  return (
    <span
      ref={anchorRef}
      style={{ display: "inline-block" }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setVisible(false)}>
      {children}

      {visible &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 9999,
              visibility: coords.top === 0 && coords.left === 0 ? "hidden" : "visible",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  );
};

const InfoIcon = ({ width = 32, height = 32, onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        width,
        height,
        borderRadius: "50%",
        border: "none",
        background: "#f97316",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 8px 14px rgba(0,0,0,0.14)",
        padding: 0,
      }}
    >
      <svg
        width={width * 0.55}
        height={height * 0.55}
        viewBox="0 0 24 24"
        fill="white"
        stroke="white"
        strokeWidth="0.5"
        style={{ display: "block" }}
      >
        <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
        <path d="M19 2 L19.8 5.2 L23 6 L19.8 6.8 L19 10 L18.2 6.8 L15 6 L18.2 5.2 Z" />
        <path d="M5 15 L5.6 17.4 L8 18 L5.6 18.6 L5 21 L4.4 18.6 L2 18 L4.4 17.4 Z" />
      </svg>
    </button>
  );
};

const SupplyDetailsModal = ({ actions, ctx }) => {

  const { onClose } = actions || {};

  const {
    hierarchyInfo,
    selectedRowInfo,
    storesBreakdown,
  } = ctx || {};

  const { 
    inventoryGap,
    lostSalesRisk,
    percentValues,
    lists,
    qty
  } = selectedRowInfo || {};

  const {
    pctStockoutLegacyProducts,
    pctReplenishmentCd,
    pctPurchaseNeeded,
    pctAssortmentIssue
  } = percentValues || {};

  const { listSkusPurchaseNeeded, listSkusReplenishment } = lists || {};
  const parseSkuList = (raw) => {
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return raw.replace(/[\]]/g, '').split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  const parsedListSkusReplenishment = parseSkuList(listSkusReplenishment);
  const parsedListSkusPurchaseNeeded = parseSkuList(listSkusPurchaseNeeded);

  const { 
    qtyAssortmentIssue,
    qtyReplenishmentCd,
    qtyPurchaseNeeded
  } = qty || {};

  const [activeTab, setActiveTab] = useState(RETAIL_INVENTORY_QUALITY_TABS.AVOID_LOSS);

  const hierarchy = buildHierarchyInfo({ hierarchyInfo });

  const { level: targetLevel, additionalLevels: targetAdditionalLevels } = hierarchyInfo || {};

  const lossByStoreRanking = useMemo(() => {
    if (!targetLevel || !storesBreakdown?.length) return [];

    const targetAdditionalLevelsKey = JSON.stringify(targetAdditionalLevels || {});

    return storesBreakdown
      .map(store => {
        const entry = store?.levels?.find(item =>
          item.level === targetLevel &&
          JSON.stringify(item.additionalLevels || {}) === targetAdditionalLevelsKey
        );

        if (!entry || !Number(entry.lostSalesRisk)) return null;

        return {
          storeId: store.storeId,
          lostSalesRisk: Number(entry.lostSalesRisk),
          percentageOfShortage: Number(entry.percentageOfShortage) || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.lostSalesRisk - a.lostSalesRisk)
      .slice(0, 10);
  }, [storesBreakdown, targetLevel, targetAdditionalLevels]);

  return (
    <div style={overlayStyle}>
      <div style={{
        ...modalStyle,
        textAlign: "left",
        alignItems: "stretch",
        overflowY: "auto",
        border: "2px solid #CBD5E1",
        background: SYSTEM_COLORS.WHITE.WHITE,
        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: 'column',
        inlineSize: "44%",
        padding: "0.75rem" }}>


        <section style={{ display: "grid", gridTemplateColumns: "6fr auto" }}>
          <hgroup style={{ marginBlockEnd: "1rem" }}>
            <h5 style={{ fontWeight: 700, color: "#111827", margin: 0 }}>
              {t("storeDetails")}
            </h5>
            
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <p style={{ fontSize: "13px", textDecoration: 'bold', fontWeight: 'bold', color: "#111827" }}>
                {`${t("family")}: `}
              </p>

              <p style={{ fontSize: "13px", color: "#475569" }}>
                {hierarchy?.join(' | ')}
              </p>   
            </div>
          </hgroup>

          <button
            onClick={onClose}
            style={{
              width: 28, height: 28,
              border: 'none', borderRadius: 6,
              backgroundColor: '#fff',
              color: '#6B7280',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 'bold',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E5E7EB'; e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}>
            ×
          </button> 
        </section>

        <section>
          {/* Tabs */}
          <div style={{ 
            display: "flex",
            inlineSize: "fit-content",
            backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
            borderRadius: "0.4rem 0.4rem 0 0",
            border: "1px solid #e5e7eb",
            borderBottom: "none", }}>
            <button
              className="tab-btn"
              style={TAB_STYLE(activeTab === RETAIL_INVENTORY_QUALITY_TABS.AVOID_LOSS)}
              onClick={() => setActiveTab(RETAIL_INVENTORY_QUALITY_TABS.AVOID_LOSS)}>
              {t("avoidSalesLoss")}
            </button>

            <button
              className="tab-btn"
              style={TAB_STYLE(activeTab === RETAIL_INVENTORY_QUALITY_TABS.LOSS_BY_STORE)}
              onClick={() => setActiveTab(RETAIL_INVENTORY_QUALITY_TABS.LOSS_BY_STORE)}>
              {t("lossByStore")}
            </button>
          </div>

          {activeTab === RETAIL_INVENTORY_QUALITY_TABS.AVOID_LOSS && (
            <div style={{
              backgroundColor: "#f3f4f6",
              borderRadius: "0 0 12px 12px",
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem" }}>

              {/* Cards superiores */}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <div style={{
                  flex: 1, background: '#fff', borderRadius: 12,
                  border: '1px solid #fecaca', padding: '0.7rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: '0.6rem', fontWeight: 600, marginBottom: 8 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: '#fee2e2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/>
                        <path d="M12 9v4"/>
                        <path d="M12 17h.01"/>
                      </svg>
                    </span>
                    {t("quantityShort")}
                  </div>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>{createIntFormatter().format(inventoryGap)}</span>
                </div>

                <div style={{
                  flex: 1, background: '#fff', borderRadius: 12,
                  border: '1px solid #fed7aa', padding: '0.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: '0.75rem', fontWeight: 600, marginBottom: 8 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: '#fff7ed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 17h6v-6"/>
                        <path d="m22 17-8.5-8.5-5 5L2 7"/>
                      </svg>
                    </span>
                    {t("riskSalesLostColumn")}
                  </div>
                  <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>{currencyFormatter.format(lostSalesRisk)}</span>
                </div>
              </div>

              {/* Ações Sugeridas */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '1rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ fontSize: '1rem' }}>
                    {t("suggestedActions")}
                  </strong>
                  <p style={{ fontSize: '0.8rem', color: '#6B7280', margin: '4px 0 0' }}>
                    Análise de como suprir os <strong>{createIntFormatter().format(inventoryGap)}</strong> itens em falta.
                  </p>
                </div>

                {/* Barra laranja */}
                <div style={{ height: 3, background: '#F97316', borderRadius: 2, margin: '0.75rem 0' }} />

                {/* Linha de abastecimento */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingBlock: '0.75rem', borderBottom: '1px solid #F3F4F6' }}>
                  <DeliveryIcon uiState={{ hasActions: !!pctReplenishmentCd, size: 28 }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 60 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#22c55e' }}>
                      {createPercentFormatter().format(Number(pctReplenishmentCd) || 0)}
                    </span>

                    <span style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                    {`| ${createIntFormatter().format(qtyReplenishmentCd)}` }
                    </span>
                  </div>

                  <div>
                    <p style={{ margin: 0, fontSize: '0.8rem' }}>
                      <strong>{`${qtyReplenishmentCd} produtos` || 0} </strong>
                      estão no CD e podem ser enviados para a Loja
                    </p>

                    <a 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!parsedListSkusReplenishment.length) return;
                        downloadCSV({ 
                          data: parsedListSkusReplenishment, 
                          fileName: 'lista-transferencia' 
                        });
                      }}
                      style={{
                        fontSize: '0.75rem',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginBlockStart: 4,
                        color: parsedListSkusReplenishment.length ? '#22c55e' : '#7c8798',
                        cursor: parsedListSkusReplenishment.length ? 'pointer' : 'not-allowed',
                        pointerEvents: parsedListSkusReplenishment.length ? 'auto' : 'none',
                      }}>

                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" 
                        stroke={parsedListSkusReplenishment.length ? '#22c55e' : '#7c8798'} 
                        strokeWidth={2}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Baixar lista de transferência
                    </a>
                  </div>
                </div>

                {/* Linha compra */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingBlock: '0.75rem', borderBottom: '1px solid #F3F4F6' }}>
                  <CartIcon uiState={{ hasActions: !!pctPurchaseNeeded, size: 28 }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 60 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#F97316' }}>
                      {createPercentFormatter().format(Number(pctPurchaseNeeded) || 0)}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                      {`| ${createIntFormatter().format(qtyPurchaseNeeded)} `}
                    </span>
                  </div>

                  <div>
                    <p style={{ margin: 0, fontSize: '0.8rem' }}>
                      <strong>{`${parsedListSkusPurchaseNeeded.length || 0 } produtos`}</strong> 
                      precisam ser comprados
                    </p>
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (!parsedListSkusPurchaseNeeded.length) return;
                        downloadCSV({ 
                          data: parsedListSkusPurchaseNeeded, 
                          fileName: 'lista-compra'
                        });
                      }}
                      style={{
                        fontSize: '0.75rem',
                        color: parsedListSkusPurchaseNeeded.length ? '#F97316' : '#7c8798',
                        cursor: parsedListSkusPurchaseNeeded.length ? 'pointer' : 'not-allowed',
                        pointerEvents: parsedListSkusPurchaseNeeded.length ? 'auto' : 'none',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginBlockStart: 4 }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none"
                        stroke={parsedListSkusPurchaseNeeded.length  ? "#F97316" : "#7c8798"}
                        strokeWidth={2}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Baixar lista de compra
                    </a>
                  </div>
                </div>

                {/* Linha sortimento */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingBlock: '0.75rem' }}>
                  <GridIcon uiState={{ hasActions: !!pctAssortmentIssue, size: 28 }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 60 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#6B7280' }}>
                      {createPercentFormatter().format(Number(pctAssortmentIssue) || 0)}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
                      {`| ${createIntFormatter().format(qtyAssortmentIssue)}`}
                    </span>
                  </div>

                  <p style={{ margin: 0, fontSize: '0.8rem' }}>
                    <strong>{`${createIntFormatter().format(qtyAssortmentIssue)}`} produtos</strong> não há solução com o sortimento atual
                  </p>
                </div>
              </div>

              {/* Card inferior */}
              <div style={{
                background: '#fff', borderRadius: 12,
                border: '1px solid #E5E7EB',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: '0.75rem', fontWeight: 700 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: '#dbeafe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth={2}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </span>
                  % da Qtd em Falta que pode ser assumida por estoque antigo
                </div>
                <span style={{ fontSize: '1rem', fontWeight: 700 }}>
                  {createPercentFormatter().format(Number(pctStockoutLegacyProducts) || 0)}
                </span>
              </div>

            </div>
          )}

          {activeTab === RETAIL_INVENTORY_QUALITY_TABS.LOSS_BY_STORE && (
            <div style={{
              backgroundColor: "#f3f4f6",
              borderRadius: "0 0 12px 12px",
              padding: "1rem" }}>

              <div style={{ 
                background: SYSTEM_COLORS.WHITE.WHITE, borderRadius: 12, 
                border: '1px solid #E5E7EB', padding: '1rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong style={{ fontSize: '0.8rem' }}>
                    {t("lossByStoreRankingTitle")}
                  </strong>
                  <p style={{ fontSize: '0.6rem', color: '#6B7280', margin: '4px 0 0' }}>
                    {t("lossByStoreRankingSubtitle", { family: targetLevel })}
                  </p>
                </div>

                {lossByStoreRanking.length ? (
                  <table style={{ inlineSize: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ 
                          padding: '0.6rem', paddingInlineEnd: '0.15rem', textAlign: 'left', inlineSize: "16px",
                          fontSize: '0.65rem', color: '#374151', background: '#F3F4F6', 
                          borderBottom: '1px solid #E5E7EB' }}>
                          {t("lossByStoreRankColumn")}
                        </th>

                        <th style={{ 
                          padding: '0.6rem', paddingInlineStart: '0.15rem', textAlign: 'left', fontSize: '0.65rem', 
                          color: '#374151', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                          {t("lossByStoreStoreColumn")}
                        </th>

                        <th style={{ padding: '0.6rem', textAlign: 'right', fontSize: '0.65rem', color: '#374151', 
                          background: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                          {t("lossByStoreRiskColumn")}
                        </th>

                        <th style={{ padding: '0.6rem', textAlign: 'right', fontSize: '0.65rem', color: '#374151', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                          {t("percentageOfShortage")}
                        </th>
                      </tr>
                    </thead>
                    
                    <tbody>
                      {lossByStoreRanking
                        .filter((row) => row.storeId !== COMPANY_STORE_ID.STORE_ID)
                        .map((row, index) => (
                          <tr key={row.storeId} style={{ background: index % 2 ? '#F9FAFB' : SYSTEM_COLORS.WHITE.WHITE }}>
                            <td style={{ padding: '0.6rem', paddingInlineEnd: '0.15rem', fontSize: '0.7rem', fontWeight: 700, borderBottom: '1px solid #F3F4F6' }}>
                              {`${index + 1}º`}
                            </td>
                            <td style={{ padding: '0.6rem', paddingInlineStart: '0.15rem', fontSize: '0.7rem', borderBottom: '1px solid #F3F4F6' }}>
                              {t("storeLabel", { storeId: row.storeId })}
                            </td>
                            <td style={{ padding: '0.6rem', fontSize: '0.7rem', textAlign: 'right', borderBottom: '1px solid #F3F4F6' }}>
                              {currencyFormatter.format(row.lostSalesRisk)}
                            </td>
                            <td style={{ padding: '0.6rem', fontSize: '0.7rem', textAlign: 'right', borderBottom: '1px solid #F3F4F6' }}>
                              {createPercentFormatter().format(row.percentageOfShortage)}
                            </td>
                          </tr>
                        ))}
                    </tbody>

                  </table>
                ) : (
                  <p style={{ fontSize: '0.7rem', color: '#6B7280' }}>
                    {t("lossByStoreEmptyRanking")}
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const AdjustmentsSupplyTable = ({ actions, ctx, uiState, tableId = "adjustments-supply-table" }) => {

  const { setSorting, setPage } = actions || {};
  const {
    currentStoreId,
    columns,
    data,
    supplyDetails,
    allStoresSupplyAdjustments,
    pagination
  } = ctx || {};

  const { sorting, isLoadingSupplyDetails, toggleBreakdownData } = uiState || {};

  const [selectedRow, setSelectedRow] = useState(null);
  const [hoveredLevelCellId, setHoveredLevelCellId] = useState(null);
  const [pageInput, setPageInput] = useState(String(pagination?.page ?? 1));

  const tableScrollRef = useRef(null);

  const columnVisibility = useMemo(() => ({
    curveA_avg: !!toggleBreakdownData,
    curveB_avg: !!toggleBreakdownData,
    curveC_avg: !!toggleBreakdownData,
    priceHigh_avg: !!toggleBreakdownData,
    priceMedium_avg: !!toggleBreakdownData,
    priceLow_avg: !!toggleBreakdownData,
  }), [toggleBreakdownData]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const baseWidthPct = 100 / visibleLeafColumns.length;
  const lastColumnId = visibleLeafColumns.at(-1)?.id;

  const handleRowExpand = (row) => {
    const newRow = selectedRow?.id === row.id ? null : row;
    setSelectedRow(newRow);
  };

  useEffect(() => {
    setPageInput(String(pagination?.page ?? 1));
  }, [pagination?.page]);

  const handlePageSubmit = () => {
    const page = Number(pageInput);

    if (!Number.isInteger(page) || page < 1 || page > pagination?.totalPages) {
      setPageInput(String(pagination?.page));
      return;
    }

    setPage(page);
  };  

  if (isLoadingSupplyDetails) {
    return (
      <LoadingContent label={t("loadingSupplyDetails")} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>      
    )
  };

  return (
    <div style={{ 
      background: SYSTEM_COLORS.WHITE.WHITE, borderRadius: 12, 
      border: `1px solid ${SYSTEM_COLORS.BLUE.CORNFLOWER}`, overflow: "hidden" }}>

      {pagination && pagination.totalPages > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {/* Primeira página */}
              <button
                onClick={() => setPage(1)}
                disabled={pagination.page <= 1}
                style={{
                  border: "none", background: "transparent",
                  cursor: pagination.page <= 1 ? "default" : "pointer",
                  color: pagination.page <= 1 ? "#D1D5DB" : "#374151",
                  fontSize: "1.1rem", fontWeight: 600, padding: 4, lineHeight: 1,
                }}
                aria-label="Primeira página"
                title="Primeira página">
                {"«"}
              </button>

              {/* Página anterior */}
              <button
                onClick={() => setPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  border: "none", background: "transparent",
                  cursor: pagination.page <= 1 ? "default" : "pointer",
                  color: pagination.page <= 1 ? "#D1D5DB" : "#374151",
                  fontSize: "1.1rem", padding: 4, lineHeight: 1 }}
                aria-label="Página anterior"
                title="Página anterior"
              >
                {"‹"}
              </button>

              {/* Página atual */}
              <input
                type="number" min={1} max={pagination.totalPages} value={pageInput}
                onChange={(e) => setPageInput(e.target.value)} onBlur={handlePageSubmit}
                onKeyDown={(e) => { if (e.key === "Enter") { handlePageSubmit(); e.currentTarget.blur();}}}
                style={{
                  width: 52, height: 30, padding: "2px 6px",
                  border: "1px solid #D1D5DB", borderRadius: 6, fontSize: "1rem", fontWeight: 700,
                  color: "#111827", textAlign: "center", outline: "none" }}
                aria-label="Número da página"
              />

              {/* Próxima página */}
              <button
                onClick={() => setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                style={{
                  border: "none", background: "transparent",
                  cursor: pagination.page >= pagination.totalPages ? "default" : "pointer",
                  color: pagination.page >= pagination.totalPages ? "#D1D5DB" : "#374151",
                  fontSize: "1.1rem", padding: 4, lineHeight: 1 }}
                aria-label="Próxima página"
                title="Próxima página"
              >
                {"›"}
              </button>

              {/* Última página */}
              <button
                onClick={() => setPage(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
                style={{
                  border: "none", background: "transparent",
                  cursor: pagination.page >= pagination.totalPages ? "default" : "pointer",
                  color: pagination.page >= pagination.totalPages ? "#D1D5DB" : "#374151",
                  fontSize: "1.1rem", fontWeight: 600, padding: 4, lineHeight: 1 }}
                aria-label="Última página"
                title="Última página"
              >
                {"»"}
              </button>

            </div>

            <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>
              {`${pagination.totalPages} página${pagination.totalPages > 1 ? "s" : ""}`}
            </span>
          </div>
        </div>
      )}

      <div ref={tableScrollRef} style={{ maxBlockSize: "72dvh", overflow: "auto", position: "relative" }} >
        <table id={tableId} style={{ width: "100%" }}>
          <colgroup>
            {visibleLeafColumns.map((column) => (
              <col key={column.id} style={{ width: getColumnWidth(column.id, baseWidthPct, lastColumnId) }} />
            ))}
          </colgroup>

          <thead>
            {table.getHeaderGroups().map((headerGroup, rowIndex) => {
                const isLastHeaderRow = rowIndex === table.getHeaderGroups().length - 1;

                return (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.id === "expand" ? "hide-on-export" : ""}
                      style={{
                        padding: header.isPlaceholder ? 0 : 10,
                        textAlign: "center",
                        background: SYSTEM_COLORS.WHITE.WHITE,

                        borderBottom: (isLastHeaderRow || !header.isPlaceholder)
                            ? `1px solid ${SYSTEM_COLORS.GRAY.COOL_GRAY}`
                            : "none",

                        borderRight: `1px solid ${SYSTEM_COLORS.BLUE.CORNFLOWER}`,

                        position: "sticky",
                        zIndex: 30,
                        top: rowIndex * HEADER_HEIGHT,
                      }}

                      onClick={() => {
                        const current = sorting?.find(s => s.id === header.column.id);
                        let next;

                        if (!current) {
                          next = { id: header.column.id, desc: false };
                        } else {
                          next = { id: header.column.id, desc: !current.desc };
                        }
                        setSorting([next]);
                      }}
                    >
                        {!header.isPlaceholder && (
                        <div
                            style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 6,
                            }}
                        >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </div>
                        )}
                    </th>
                    ))}
                </tr>
                );
            })}
          </thead>

          <tbody>

            {table.getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  style={{ background: row.index % 2 ? SYSTEM_COLORS.GRAY.GRAY_50 : SYSTEM_COLORS.WHITE.WHITE }}>
                  {row.getVisibleCells().map((cell) => {
                    const isLevelCell = cell.column.id === "level";
                    const isHoveredLevelCell = isLevelCell && hoveredLevelCellId === cell.id;

                    return (
                      <td
                        key={cell.id}
                        className={cell.column.id === "expand" ? "hide-on-export" : ""}
                        data-v={
                          cell.column.id === "areas"
                            ? getActiveAreasExportLabel(row.original)
                            : undefined
                        }
                        style={{
                          padding: 10,
                          textAlign: cell.column.id === "level" ? "left" : "center",
                          borderBottom:  `1px solid ${SYSTEM_COLORS.BLUE.CORNFLOWER}`,
                          borderRight:  `1px solid ${SYSTEM_COLORS.BLUE.CORNFLOWER}`,
                          background: "inherit",
                          overflow: "visible",
                        }}>
                        {cell.column.id === "expand" ? (
                          <div
                            style={{
                              width: 56,
                              minWidth: 56,
                              maxWidth: 56,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              zIndex: 5,
                            }}
                          >
                            <InfoIcon
                              isOpen={selectedRow?.id === row.id}
                              onClick={() => handleRowExpand(row)}
                            />
                          </div>
                        ) : isLevelCell ? (
                          <span
                            onMouseEnter={() => setHoveredLevelCellId(cell.id)}
                            onMouseLeave={() => setHoveredLevelCellId(null)}
                            style={{
                              display: "inline-block",
                              padding: "4px 6px",
                              borderRadius: "0.4rem",
                              background: isHoveredLevelCell ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE_12 : "transparent",
                              cursor: isHoveredLevelCell ? "pointer" : "default",
                            }}
                          >
                            <TooltipBase
                              content={
                                <SupplyGradeAnalysisCard ctx={{ 
                                  levelEntry: row?.original, 
                                  familyLabel: row?.original?.level,
                                  productId: row?.original?.productId
                                }}/>
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell ?? cell.column.columnDef.accessorKey,
                                cell.getContext()
                              )}
                            </TooltipBase>
                          </span>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell ?? cell.column.columnDef.accessorKey,
                            cell.getContext()
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

          </tbody>
        </table>
      </div>

      {selectedRow && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => { setSelectedRow(null) }}>
          <div onClick={(e) => e.stopPropagation()}>
            <SupplyDetailsModal
              actions={{ onClose: () => { setSelectedRow(null) } }}
              ctx={{
                supplyDetails: supplyDetails,
                storeId: currentStoreId,
                storesBreakdown: allStoresSupplyAdjustments,
                hierarchyInfo: {
                  level: selectedRow.original.level,
                  additionalLevels: selectedRow.original.additionalLevels
                },
                selectedRowInfo: {
                  inventoryGap: selectedRow?.original?.inventoryGap,
                  lostSalesRisk: selectedRow?.original?.lostSalesRisk,
                  percentageOfShortage: selectedRow?.original?.percentageOfShortage,
                  percentValues: {
                    pctStockoutLegacyProducts: selectedRow?.original?.pctStockoutLegacyProducts,
                    pctReplenishmentCd: selectedRow?.original?.pctReplenishmentCd,
                    pctAssortmentIssue: selectedRow?.original?.pctAssortmentIssue,
                    pctPurchaseNeeded: selectedRow?.original?.pctPurchaseNeeded,
                  },
                  lists: {
                    listSkusPurchaseNeeded: selectedRow?.original?.listSkusPurchaseNeeded,
                    listSkusReplenishment: selectedRow?.original?.listSkusReplenishment
                  },
                  qty: {
                    qtyAssortmentIssue: selectedRow?.original?.qtyAssortmentIssue,
                    qtyPurchaseNeeded: selectedRow?.original?.qtyPurchaseNeeded,
                    qtyReplenishmentCd: selectedRow?.original?.qtyReplenishmentCd
                  }
                },
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export function AdjustmentsSupply() {

  const [currentCompanyArea, setCompanyArea] = useState([]);
  const [toggleBreakdownData, setToggleBreakdownData] = useState(false);
  const [page, setPage] = useState(1);

  const vm = useOtbViewModel({
    shouldFetchSupplyAdjustments: true,
    page,
    pageSize: 450
  });

  const vmAnalysis = useAnalysisViewModel({ shouldFetchForecastData: true });

  const handleSetSelectedStore = (storeId) => {
    setPage(1);
    vm.actions?.setSelectedStore(storeId);
  };

  const tableCtx = useMemo(() => ({
    columns: vm?.ctx?.supplyAdjustmentColumns || [],
    data: (vm?.ctx?.tableData || []).filter(row =>
      !currentCompanyArea.length ||
      currentCompanyArea.some(areaId => row[areaFieldMap[areaId]])
    ),
    currentStoreId: vm.ctx?.selectedStore,
    allStoresSupplyAdjustments: vm?.ctx?.allStoresSupplyAdjustments,
    pagination: vm?.ctx?.pagination,
  }), [
    vm?.ctx?.supplyAdjustmentColumns,
    vm?.ctx?.tableData,
    vm?.ctx?.selectedStore,
    vm?.ctx?.allStoresSupplyAdjustments,
    vm?.ctx?.pagination,
    currentCompanyArea,
  ]);

  const tableUiState = useMemo(() => ({
    sorting: vm?.uiState?.sorting || [],
    toggleBreakdownData: toggleBreakdownData
  }), [
    vm?.uiState?.sorting,
    toggleBreakdownData
  ]);

  const headerCtx = useMemo(() => ({
    season: vm.ctx?.season,
    allAvailableSeasons: vm.ctx?.allAvailableSeasons,
    data: vm?.ctx?.allStoresIds || [],
    currentStoreId: vm.ctx?.selectedStore,
    selectedSecondaryFilterValues: vm.ctx?.searchLevel,
    currentCompanyArea: currentCompanyArea,
    areas: vm?.ctx?.assortmentAreas,
    toggleBreakdownData: toggleBreakdownData
  }), [
    vm.ctx?.season,
    vm.ctx?.allAvailableSeasons,
    vm?.ctx?.allStoresIds,
    vm.ctx?.selectedStore,
    vm.ctx?.searchLevel,
    currentCompanyArea,
    vm?.ctx?.assortmentAreas,
    toggleBreakdownData,
  ]);

  if (vm?.uiState?.isLoadingSupplyAdjustments) {
    return (
      <LoadingContent label={"Carregando dados de abastecimento..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>
    )
  }

  return (
    <Default>
      <div style={{ paddingBlockStart: "1rem", marginInlineStart: 'var(--menu-width-collapsed)'}}>
        <div style={{ marginBlockEnd: "2rem" }}>
          <CustomHeading
            size="lg"
            margin="0"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="700"
            color="greyStrong6"
            fontSize={["1.5rem", "1.5rem", "1.5rem"]}>
            {t("adjustmentPageTitle")}
          </CustomHeading>
          <CustomText margin="0 0" size="xs">
            {`${t("forecastGeneratedIn")} ${vmAnalysis?.ctx?.generationDate || "01/01/1970"}`}
          </CustomText>
        </div>

          <HeaderWithFilters 
            actions={{
              setCurrentStoreData: handleSetSelectedStore,
              setSelectedSecondaryFilterValues: vm.actions?.setSearchLevel,
              setSeason: vm.actions?.setSeason,
              setCompanyArea: setCompanyArea,
              setToggleBreakdownData: setToggleBreakdownData
            }}
            ctx={headerCtx}
            mode = {HEADER_STRATEGY_CONSTANTS.RETAIL}/>

        <section style={{ overflowY: "auto", marginBlockStart: "2rem", paddingBlockEnd: '2rem' }}>
          <AdjustmentsSupplyTable
            actions={{ setSorting: vm?.actions?.setSorting, setPage: setPage }}
            ctx={tableCtx}
            uiState={tableUiState}/>
        </section>

      </div>
    </Default>
  );
};